import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import TyreSearchBar from "../components/TyreSearchBar";
import TyreCard from "../components/TyreCard";
import RegLookup from "../components/RegLookup";
import Seo from "../components/Seo";
import TyreSizeGuide from "../components/TyreSizeGuide";
import { fetchTyres, fetchTyreCount } from "../lib/api";

const BRANDS = ["Michelin", "Bridgestone", "Continental", "Pirelli", "Goodyear", "Dunlop", "Hankook", "Yokohama"];
const SEASONS = ["Summer", "Winter", "All-Season"];
const WET_GRIPS = ["A", "B"];
const FUEL_GRADES = ["A", "B", "C"];

const TyresList = () => {
  const [params, setParams] = useSearchParams();
  const [tyres, setTyres] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [brands, setBrands] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [wetGrips, setWetGrips] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [priceMax, setPriceMax] = useState(250);
  const [ratingMin, setRatingMin] = useState(0);
  const [sort, setSort] = useState("price_asc");
  const [page, setPage] = useState(1);
  const pageSize = 9;

  const sizeFilters = useMemo(() => ({
    width: params.get("width") || "",
    profile: params.get("profile") || "",
    rim: params.get("rim") || "",
  }), [params]);

  const query = useMemo(() => {
    const q = {
      sort,
      page,
      page_size: pageSize,
      price_min: 0,
      price_max: priceMax,
    };
    if (sizeFilters.width) q.width = sizeFilters.width;
    if (sizeFilters.profile) q.profile = sizeFilters.profile;
    if (sizeFilters.rim) q.rim = sizeFilters.rim;
    if (brands.length) q.brand = brands.join(",");
    if (seasons.length) q.season = seasons.join(",");
    if (wetGrips.length) q.wet_grip = wetGrips.join(",");
    if (fuels.length) q.fuel_efficiency = fuels.join(",");
    if (ratingMin > 0) q.rating_min = ratingMin;
    return q;
  }, [sort, page, priceMax, sizeFilters, brands, seasons, wetGrips, fuels, ratingMin]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTyres(query), fetchTyreCount(query)])
      .then(([list, c]) => { setTyres(list); setTotal(c.total || 0); })
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { setPage(1); }, [brands, seasons, wetGrips, fuels, priceMax, ratingMin, sort, sizeFilters.width, sizeFilters.profile, sizeFilters.rim]);

  const toggle = (arr, v, setter) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="vd-page" data-testid="page-tyres">
      <Seo
        title="Shop Tyres Online"
        description="Browse premium tyres by size, brand or vehicle. Free UK delivery on 2+ tyres, EU labels, next-day shipping and expert mobile fitting."
        path="/tyres"
      />
      <section className="vd-section-secondary py-12 relative overflow-hidden">
        {/* Decorative red accent lines — visible in dark mode per mockup IMG_9603 */}
        <div className="vd-red-accent-line" style={{ top: "20%" }} aria-hidden="true" />
        <div className="vd-red-accent-line" style={{ bottom: "20%" }} aria-hidden="true" />
        <div className="vd-container relative">
          <h1 className="mb-3 text-center lg:text-left">Shop Tyres by Size, Brand, or Vehicle</h1>
          <p className="vd-muted max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">Find the perfect fit for your car — premium brands, fast delivery, expert fitting.</p>
          <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 items-start">
            <TyreSearchBar showBrand />
            <RegLookup />
          </div>
        </div>
      </section>

      <section className="vd-container py-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="vd-card p-5 h-fit sticky top-[88px]" data-testid="filters-sidebar">
          <FilterGroup title="Filter by Brand">
            {BRANDS.map((b) => (
              <label key={b} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={brands.includes(b)} onChange={() => toggle(brands, b, setBrands)} data-testid={`filter-brand-${b.toLowerCase()}`} />
                <span className="vd-body text-[15px]">{b}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Filter by Season">
            {SEASONS.map((s) => (
              <label key={s} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={seasons.includes(s)} onChange={() => toggle(seasons, s, setSeasons)} data-testid={`filter-season-${s.toLowerCase()}`} />
                <span className="vd-body text-[15px]">{s}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Filter by Price">
            <div className="px-1">
              <input type="range" min="50" max="250" step="5" value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))} className="vd-range" data-testid="filter-price-range" />
              <div className="flex justify-between vd-muted text-sm mt-1">
                <span>£50</span>
                <span>£{priceMax}</span>
              </div>
            </div>
          </FilterGroup>

          <FilterGroup title="Wet Grip (EU)">
            {WET_GRIPS.map((g) => (
              <label key={g} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={wetGrips.includes(g)} onChange={() => toggle(wetGrips, g, setWetGrips)} data-testid={`filter-wet-${g.toLowerCase()}`} />
                <span className="vd-body text-[15px]">Class {g}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Fuel Efficiency (EU)">
            {FUEL_GRADES.map((g) => (
              <label key={g} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={fuels.includes(g)} onChange={() => toggle(fuels, g, setFuels)} data-testid={`filter-fuel-${g.toLowerCase()}`} />
                <span className="vd-body text-[15px]">Class {g}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Filter by Rating">
            {[4, 3].map((r) => (
              <label key={r} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="radio" name="rating" className="vd-checkbox" checked={ratingMin === r} onChange={() => setRatingMin(r)} data-testid={`filter-rating-${r}`} />
                <span className="vd-body text-[15px]">{r} Stars &amp; Up</span>
              </label>
            ))}
            <label className="flex items-center gap-3 py-1.5 cursor-pointer">
              <input type="radio" name="rating" className="vd-checkbox" checked={ratingMin === 0} onChange={() => setRatingMin(0)} data-testid="filter-rating-all" />
              <span className="vd-body text-[15px]">All ratings</span>
            </label>
          </FilterGroup>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="vd-muted text-sm" data-testid="results-count">{loading ? "Loading..." : `${total} tyres found`}</p>
            <div className="flex items-center gap-2">
              <span className="vd-muted text-sm">Sort by:</span>
              <select className="vd-select" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select">
                <option value="price_asc">Price (Low to High)</option>
                <option value="price_desc">Price (High to Low)</option>
                <option value="rating_desc">Top Rated</option>
                <option value="brand_asc">Brand (A–Z)</option>
              </select>
            </div>
          </div>

          {loading && tyres.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tyres-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="vd-card aspect-[4/5] animate-pulse" style={{ background: "var(--bg-secondary)" }} />
              ))}
            </div>
          ) : tyres.length === 0 ? (
            <div className="vd-card p-10 text-center" data-testid="tyres-empty">
              <p className="vd-muted">No tyres match your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tyres-grid">
              {tyres.map((t) => <TyreCard key={t.id} tyre={t} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className="w-10 h-10 rounded-md border font-semibold transition-colors"
                  style={{
                    borderColor: page === i + 1 ? "var(--primary-red)" : "var(--border-color)",
                    background: page === i + 1 ? "var(--primary-red)" : "var(--card-bg)",
                    color: page === i + 1 ? "#fff" : "var(--text-heading)",
                  }}
                  data-testid={`page-${i + 1}`}
                >{i + 1}</button>
              ))}
              {page < totalPages && (
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="vd-btn-secondary" data-testid="page-next">Next ›</button>
              )}
            </div>
          )}

          <div className="text-center mt-14">
            <p className="vd-muted mb-4">Need help choosing the right tyre?</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="tel:07979210250" className="vd-btn-primary" data-testid="tyres-call-us">Call Us</a>
              <Link to="/mobile-fitting" className="vd-btn-secondary" data-testid="tyres-book-mobile-fitting">Book Mobile Fitting</Link>
            </div>
          </div>
        </div>
      </section>

      <TyreSizeGuide />
    </div>
  );
};

const FilterGroup = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="vd-heading text-sm uppercase tracking-wider mb-3" style={{ letterSpacing: "0.08em" }}>{title}</h4>
    {children}
  </div>
);

export default TyresList;
